import { useEffect, useMemo, useRef, useState } from "react"

import { AlertTriangle, LayoutDashboard, Loader2, Wrench } from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { DashboardElement, DashboardSpec } from "./catalog"

import { Button } from "@/components/ui/button"
import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"

import { ActionsRegistryProvider } from "./actions-registry"
import { BindingsProvider } from "./bindings-context"
import { dashboardSpecSchema } from "./catalog"
import { DashboardElementRenderer } from "./registry"
import { TileRefContext } from "./tile-ref-context"
import { WatchRuntime } from "./watch-runtime"
import { AddTileSlot } from "../components/add-tile-slot"
import { DashboardFilterBar } from "../components/dashboard-filter-bar"
import { QualityReviewBanner } from "../components/quality-review-banner"
import { SortableDashboard, SortableItem } from "../components/sortable-dashboard"
import { SpecIssuesBanner } from "../components/spec-issues-banner"
import { DashDevtoolsPanel } from "../devtools/dash-devtools-panel"
import { DashDevtoolsProvider, shouldEnableDashDevtools } from "../devtools/devtools-context"
import { DashboardStateProvider } from "../state/DashboardStateProvider"
import { DashboardUrlStateSync } from "../state/url-state-sync"
import { getTopLevelTileSpanClass, insertManualTile } from "../utils/manual-tile"
import { validateSpec } from "../utils/spec-validator"

// Specs we've already warned about — keyed by spec object identity so
// re-renders with the same spec don't spam the console. WeakSet so
// dropped specs are garbage-collected.
const warnedSpecs = new WeakSet<object>()

type DashboardRendererProps = {
  spec: DashboardSpec | null
  /** Id of the persisted dashboard, when this renderer is showing a saved
   *  dashboard rather than a chat-only preview. Per-tile refresh threads
   *  it through BindingsProvider so the refresh button knows which doc to
   *  hit. Leave undefined for ephemeral chat-preview renders. */
  dashboardId?: string
  /** Most recent persisted quality review for this dashboard, when the
   *  page-level loader has it. Surfaced via the QualityReviewBanner so
   *  the user sees a score + issues immediately on load. */

  initialQualityReview?: import("../services/dashboard-service").QualityReview | null
  /** Append-only review history snapshots for the sparkline. */

  qualityHistory?: import("../services/dashboard-service").QualityHistoryEntry[]
  /** Called when a quality-review fix endpoint returned a fresh
   *  dashboard envelope. Host should re-load it into the dash store
   *  rather than triggering a page reload (the open dashboard isn't
   *  deep-linked, so reload drops the user back to the landing). */

  onDashboardRefreshed?: (updated: import("../services/dashboard-service").SavedDashboard) => void
  className?: string
  selectedTileIndex?: number | null
  onSelectTile?: (index: number) => void
  tileRefMap?: Map<number, HTMLDivElement>
  canEdit?: boolean
  onSpecChange?: (spec: DashboardSpec) => void
  onStateReady?: (getState: () => Record<string, unknown>) => void
  /**
   * Receives the live state store so callers outside the renderer
   * (the bookmark menu's "apply view") can write back without
   * needing to be a descendant. Stable identity expected — see
   * DashboardStateProvider.
   */

  onStoreReady?: (store: import("../state/dashboard-state").DashboardStateStore) => void
  /**
   * True while the dashboard's queryBindings are being re-run. Tiles
   * with a binding render a skeleton overlay until the new spec lands.
   */
  isRefreshing?: boolean
  /**
   * Sync dashboard state to the URL `?dash_state=…` param so reload
   * and shared links preserve filters. Defaults to true; set false in
   * mini-previews.
   */
  syncStateToUrl?: boolean
  /**
   * Callback fired when the user describes a tile via the "+ Add tile"
   * slot (edit mode only). Receives the user's natural-language
   * description and the insertion index. The host is expected to
   * dispatch the description into the chat pipeline so the agent
   * generates a yaml-edit fence inserting one tile at the index.
   * When omitted, the slot stays hidden — the renderer doesn't
   * show an affordance the host can't service.
   */
  onAddTile?: (description: string, insertionIndex: number) => void
  /**
   * Called when a state path referenced by a binding's
   * `params_from_state` changes — the cue to re-run the relevant
   * SQL. Hook fires debounced so back-to-back filter toggles collapse
   * into one round-trip. When omitted, filters mutate state but no
   * server-side refresh runs (use case: chat preview).
   */
  onAutoRefresh?: () => void | Promise<void>
}

export function DashboardRenderer({
  spec,
  dashboardId,
  initialQualityReview,
  qualityHistory,
  onDashboardRefreshed,
  className,
  selectedTileIndex,
  onSelectTile,
  tileRefMap,
  canEdit,
  onSpecChange,
  onStateReady,
  onStoreReady,
  isRefreshing = false,
  syncStateToUrl = true,
  onAddTile,
  onAutoRefresh,
}: DashboardRendererProps) {
  const { t } = useTranslation()
  const indexCounter = useRef({ current: 0 })

  const validationResult = useMemo(() => {
    if (!spec) return { valid: false as const, error: null }
    const result = dashboardSpecSchema.safeParse(spec)
    if (result.success) {
      return { valid: true as const, data: result.data }
    }
    return { valid: false as const, error: result.error.message }
  }, [spec])

  // §2.5 — structural validator runs alongside the Zod parse and emits
  // dev-only console warnings for the AI-shaped slips Zod can't catch
  // (e.g. `visible` placed inside `props` instead of top-level). Issue
  // codes are stable so devtools / future agent retry logic can react;
  // here we just surface them so the spec author sees them in the
  // console with a single warn per spec identity. Hoisted above the
  // early returns so it runs on every render in the same order
  // (Rules of Hooks).
  const dashboardForValidation = validationResult.valid ? validationResult.data : null
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return
    if (!dashboardForValidation || warnedSpecs.has(dashboardForValidation)) return
    const result = validateSpec(dashboardForValidation)
    if (result.issues.length === 0) return
    warnedSpecs.add(dashboardForValidation)
    console.warn(
      "[dash] spec-validator found %d issue(s):\n%s",
      result.issues.length,
      result.issues
        .map((i) => `  • [${i.code}${i.elementPath ? ` @ ${i.elementPath}` : ""}] ${i.message}`)
        .join("\n")
    )
  }, [dashboardForValidation])

  if (!spec) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <LayoutDashboard className="text-muted-foreground size-12" />
        <p className="text-muted-foreground text-sm">{t("dash.dashboard.no_spec")}</p>
      </div>
    )
  }

  if (!validationResult.valid) {
    return (
      <InvalidSpecAutoRepair
        dashboardId={dashboardId}
        error={validationResult.error}
        onDashboardRefreshed={onDashboardRefreshed}
      />
    )
  }

  const dashboard = validationResult.data
  const canAddTiles = Boolean(canEdit && (onAddTile || onSpecChange))
  const handleAddManualTile = onSpecChange
    ? (tile: DashboardElement, insertionIndex: number) => {
        onSpecChange({
          ...dashboard,
          children: insertManualTile(dashboard.children, tile, insertionIndex),
        })
      }
    : undefined
  // Reset index counter on each render
  indexCounter.current.current = 0

  // Devtools mount is decided once per render; the gate itself is cheap
  // and only true when ?debug=dash is on the URL or localStorage opts in.
  const devtoolsEnabled = shouldEnableDashDevtools()

  const content = (
    <DashboardStateProvider
      initialState={dashboard.state as Record<string, unknown> | undefined}
      onStateReady={onStateReady}
      onStoreReady={onStoreReady}
    >
      <DashboardUrlStateSync enabled={syncStateToUrl} />
      {/* Pass `dashboard` unconditionally — the provider's effect
          early-returns on null, so passing it null on the first render
          (when devtoolsEnabled flips on) means the very first
          spec-changed event is never recorded. The provider itself is
          cheap when observeActions is false. */}
      <DashDevtoolsProvider observeActions={devtoolsEnabled} spec={dashboard}>
        <ActionsRegistryProvider>
          <WatchRuntime bindings={dashboard.watchBindings}>
            <BindingsProvider
              bindings={dashboard.queryBindings}
              canViewSql={canEdit ?? true}
              dashboardId={dashboardId}
              isRefreshing={isRefreshing}
            >
              <div className={className}>
                {dashboard.title && (
                  <h2 className="text-foreground mb-4 text-lg font-semibold">{dashboard.title}</h2>
                )}
                {/* Filter strip + auto-refresh-on-state-change. Renders
                    nothing when the dashboard has no SQL-parameter-driven
                    bindings (chat previews, hand-authored specs). Mounted
                    inside DashboardStateProvider so it can read the
                    live store via context. */}
                <DashboardFilterBar
                  isRefreshing={isRefreshing}
                  spec={dashboard}
                  onAutoRefresh={onAutoRefresh}
                />
                {canEdit && <SpecIssuesBanner spec={dashboard} onSpecChange={onSpecChange} />}
                {/* Server-side AI review of the saved dashboard. Opt-in CTA
              renders for every loaded dashboard; user clicks to fire
              the review. Hidden for chat-only previews (no dashboardId)
              and for read-only viewers — the review/fix endpoints
              mutate the persisted spec, so a reader hitting the CTA
              would 404 against the owner-only lookup. Surfacing it
              without the affordance keeps the UI honest. */}
                {dashboardId && canEdit && (
                  <QualityReviewBanner
                    canAutoGenerateMissingTiles={Boolean(
                      dashboard.queryBindings?.some(
                        (binding) =>
                          binding.binding_type !== "web_research" && binding.connection_id
                      )
                    )}
                    dashboardId={dashboardId}
                    history={qualityHistory}
                    initialReview={initialQualityReview}
                    onDashboardRefreshed={onDashboardRefreshed}
                  />
                )}
                <SortableDashboard
                  canEdit={canEdit}
                  // Drag is no longer globally disabled when any tile is
                  // selected — that caused a "tiles can't be moved any
                  // longer" regression where one click on any tile killed
                  // drag for every other tile until the user clicked
                  // outside. The user almost never wants to drag the very
                  // tile they just opened in an edit popover; for the
                  // others, drag should keep working.
                  spec={dashboard}
                  renderOverlay={(i) => (
                    <DashboardElementRenderer
                      element={dashboard.children[i]}
                      elementPath={`children[${i}]`}
                    />
                  )}
                  onSpecChange={onSpecChange}
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {dashboard.children.map((element, i) => {
                      const renderer = (
                        <DashboardElementRenderer
                          element={element}
                          elementPath={`children[${i}]`}
                          indexCounter={indexCounter.current}
                          selectedTileIndex={selectedTileIndex}
                          onSelectTile={onSelectTile}
                        />
                      )
                      return (
                        <div key={i} className={getTopLevelTileSpanClass(element)}>
                          {canAddTiles && (
                            <AddTileSlot
                              insertionIndex={i}
                              variant="between"
                              onAddTile={onAddTile}
                              onAddManualTile={handleAddManualTile}
                            />
                          )}
                          {canEdit ? (
                            <SortableItem id={`child-${i}`}>{renderer}</SortableItem>
                          ) : (
                            renderer
                          )}
                        </div>
                      )
                    })}
                    {/* End-of-dashboard "+ Add tile" slot. Mounted only in
                  edit mode AND when the host wired an onAddTile
                  callback — otherwise the affordance has no destination
                  and would just confuse. The slot itself is always
                  partially visible (variant=end) so an empty board has
                  a clear entry point. */}
                    {canAddTiles && (
                      <AddTileSlot
                        className="md:col-span-2"
                        insertionIndex={dashboard.children.length}
                        variant="end"
                        onAddTile={onAddTile}
                        onAddManualTile={handleAddManualTile}
                      />
                    )}
                  </div>
                </SortableDashboard>
              </div>
            </BindingsProvider>
          </WatchRuntime>
        </ActionsRegistryProvider>
        {devtoolsEnabled && <DashDevtoolsPanel />}
      </DashDevtoolsProvider>
    </DashboardStateProvider>
  )

  if (tileRefMap) {
    return <TileRefContext.Provider value={tileRefMap}>{content}</TileRefContext.Provider>
  }

  return content
}

/**
 * Shown instead of the raw Zod error blob when the spec fails
 * validation. Auto-fires the backend's spec-repair endpoint on mount,
 * then hands the cleaned dashboard back to the parent so the renderer
 * re-validates on the next frame. Falls back to a manual retry button
 * if the auto-repair errors out.
 */
function InvalidSpecAutoRepair({
  dashboardId,
  error,
  onDashboardRefreshed,
}: {
  dashboardId?: string
  error: string | null

  onDashboardRefreshed?: (updated: import("../services/dashboard-service").SavedDashboard) => void
}) {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<"running" | "done" | "failed" | "no-id">(
    dashboardId ? "running" : "no-id"
  )
  const [repairError, setRepairError] = useState<string | null>(null)
  const [summary, setSummary] = useState<{
    colors_added?: number
    table_data_dropped?: number
    tiles_dropped?: number
  } | null>(null)
  const triggeredRef = useRef(false)

  async function runRepair() {
    if (!dashboardId || triggeredRef.current) return
    triggeredRef.current = true
    setPhase("running")
    setRepairError(null)
    try {
      const res = await api.post<
        unknown,
        import("../services/dashboard-service").SavedDashboard & {
          repair_summary?: typeof summary
        }
      >(`/dashboards/${dashboardId}/repair-spec`, {})
      setSummary(res.repair_summary ?? null)
      onDashboardRefreshed?.(res)
      setPhase("done")
    } catch (err) {
      logger.warn("Spec auto-repair failed", err as Error)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      setRepairError(String((err as Error)?.message ?? err))
      setPhase("failed")
      triggeredRef.current = false
    }
  }

  // Auto-fire on mount.
  useEffect(() => {
    if (phase === "running") void runRepair()
  }, [])

  if (phase === "no-id") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <AlertTriangle className="text-warning-ink size-12" />
        <p className="text-muted-foreground text-sm">{t("dash.dashboard.render_error")}</p>
        {error && (
          <pre className="text-muted-foreground max-w-md overflow-auto text-xs">{error}</pre>
        )}
      </div>
    )
  }

  if (phase === "running") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <Loader2 className="text-primary size-10 animate-spin" />
        <p className="text-foreground text-sm font-medium">
          {t("dash.dashboard.auto_repair_running", "Reparerar dashboarden automatiskt...")}
        </p>
        <p className="text-muted-foreground max-w-md text-xs">
          {t(
            "dash.dashboard.auto_repair_hint",
            "Specet behövde städas (saknade färger eller ogiltiga rutor). Detta tar några sekunder."
          )}
        </p>
        {summary && (
          <p className="text-muted-foreground text-xs">
            {summary.colors_added ?? 0} färger tillagda · {summary.tiles_dropped ?? 0} brutna rutor
            borttagna
          </p>
        )}
      </div>
    )
  }

  if (phase === "done") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <Wrench className="text-primary size-10" />
        <p className="text-foreground text-sm font-medium">
          {t("dash.dashboard.auto_repair_done", "Dashboarden reparerades automatiskt.")}
        </p>
        <p className="text-muted-foreground max-w-md text-xs">
          {t(
            "dash.dashboard.auto_repair_done_hint",
            "Om den här vyn ligger kvar behöver sidan laddas om eller reparationen köras igen."
          )}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            triggeredRef.current = false
            void runRepair()
          }}
        >
          <Wrench className="mr-1.5 size-3.5" />
          {t("dash.dashboard.auto_repair_retry", "Försök igen")}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <AlertTriangle className="text-warning-ink size-12" />
      <p className="text-foreground text-sm font-medium">
        {t("dash.dashboard.auto_repair_failed", "Kunde inte reparera dashboarden automatiskt.")}
      </p>
      {repairError && <p className="text-muted-foreground max-w-md text-xs">{repairError}</p>}
      <Button size="sm" variant="default" onClick={runRepair}>
        <Wrench className="mr-1.5 size-3.5" />
        {t("dash.dashboard.auto_repair_retry", "Försök igen")}
      </Button>
      {error && (
        <details className="text-muted-foreground max-w-md text-xs">
          <summary>{t("dash.dashboard.auto_repair_details", "Visa tekniskt fel")}</summary>
          <pre className="overflow-auto">{error}</pre>
        </details>
      )}
    </div>
  )
}
